import {FrameRate, TimeFormat, toFrames} from '@accurate-player/accurate-player-core';
import {QcJobItemMedia, QcJobItemMediaAudio, TimedText} from '@pluto/rest/lib/qc-types';
import {getQCJobMedia} from '@pluto/rest/lib/rest';
import {Action, ActionFunction1, ActionFunctionAny, createAction} from 'redux-actions';

export const QC_MEDIA_ITEM_SET = 'QC_MEDIA_ITEM_SET';
export const QC_MEDIA_ITEM_RESET = 'QC_MEDIA_ITEM_RESET';

export type Video = {
    id: number,
    dropFrame: boolean;
    frameRate: FrameRate;
    src: string;
    duration: number;
};

export type Channel = {
    label: string;
    waveformUrl: string;
};

export type Audio = {
    label: string;
    language: string;
    src: string;
    waveformUrl: string;
    channels: Channel[];
};

export type Item = {
    id: number;
    video: Video;
    audioTracks: Audio[];
    thumbnails: string;
    blackFrames: string;
    adBreaks: string;
    markups: string;
    notes: string;
    timedTexts: TimedText[];
};

export type SetItem = (item: Item) => void;
export type ResetItem = () => void;
export type ItemPayload = Item | null;

export const parseAudio = (qcAudio: QcJobItemMediaAudio): Audio => {
    const {audioFileUrl, label, language, waveformUrl, channels} = qcAudio;

    return {
        label,
        language,
        waveformUrl,
        src: audioFileUrl,
        channels,
    };
};

export const parseItemMedia = async (response: QcJobItemMedia): Promise<ItemPayload> => {
    const {
        id,
        audio,
        videoFile,
        thumbnails,
        blackFrames,
        adBreaks,
        markups,
        notes,
        timedTexts,
    } = response;

    const {
        dropFrame,
        framerateNumerator,
        framerateDenominator,
        url,
        totalRunTimeMilliseconds,
        id: videoFileId,
    } = videoFile;

    const frameRate: FrameRate = {
        numerator: framerateNumerator,
        denominator: framerateDenominator,
    };
    const durationMilliseconds = parseInt(totalRunTimeMilliseconds, 10);

    const duration = toFrames(
        durationMilliseconds,
        TimeFormat.Milliseconds,
        frameRate.numerator / frameRate.denominator,
        dropFrame,
        durationMilliseconds / 1000
    );

    const audioTracks = audio?.map(parseAudio);

    return {
        id,
        video: {
            id: videoFileId,
            dropFrame,
            frameRate,
            src: url,
            duration,
        },
        audioTracks,
        thumbnails,
        blackFrames,
        adBreaks,
        markups,
        notes,
        timedTexts,
    };
};

export const setItemAction = createAction<Promise<ItemPayload>, string>(
    QC_MEDIA_ITEM_SET,
    async (id: string): Promise<ItemPayload> => {
        try {
            const media = await getQCJobMedia(id);

            return parseItemMedia(media);
        } catch (error) {
            console.error(error);

            return null;
        }
    }
);

export const resetItemAction = createAction(QC_MEDIA_ITEM_RESET);

export type SetItemAction = ActionFunction1<string, Action<Promise<ItemPayload>>>;

export type ResetItemAction = ActionFunctionAny<Action<any>>;
